/* eslint-disable no-console */
import referencePool from '../modules/referencePool.js';

class ChangeHistoryResult {
  static name = 'ChangeHistoryResult';

  constructor(summary, scattData) {
    this.summary = summary;
    this.scattData = scattData;
  }
}

function applyChanges(scattData, timelineDataToDelete, timelineDataToAdd, timelineDataDifference, referenceObjToRegister, referenceObjToUnregister) {
  for (let timelineDataId of Object.keys(timelineDataToDelete)) {
    delete scattData.timelineDataSet[timelineDataId];
  }
  for (let [ timelineDataId, timelineData ] of Object.entries(timelineDataToAdd)) {
    scattData.timelineDataSet[timelineDataId] = timelineData;
  }
  for (let [ timelineDataId, timelineData ] of Object.entries(timelineDataDifference)) {
    for (let [ timelineDataKey, timelineDataValue ] of Object.entries(timelineData)) {
      if (timelineDataKey === 'segments') {
        for (let [ segmentId, segment ] of Object.entries(timelineDataValue)) {
          if (segment === undefined) {
            delete scattData.timelineDataSet[timelineDataId].segments[segmentId];
          } else {
            scattData.timelineDataSet[timelineDataId].segments[segmentId] = segment;
          }
        }
      } else {
        scattData.timelineDataSet[timelineDataId][timelineDataKey] = timelineDataValue;
      }
    }
  }

  ChangeHistory.instance.unregisterReferencePoolCallbacks();
  let referenceIdsToRegister = Object.keys(referenceObjToRegister);
  let referencesToRegister = Object.values(referenceObjToRegister);
  let numReferencesToRegister = referenceIdsToRegister.length;
  let referenceIdsToRegisterNew = referencePool.registerReferences(...referencesToRegister);
  for (let referenceIdxToRegister = 0; referenceIdxToRegister < numReferencesToRegister; ++referenceIdxToRegister) {
    let referenceIdToRegister = referenceIdsToRegister[referenceIdxToRegister];
    let referenceIdToRegisterNew = referenceIdsToRegisterNew[referenceIdxToRegister];
    delete referenceObjToRegister[referenceIdToRegister];
    referenceObjToRegister[referenceIdToRegisterNew] = referencesToRegister[referenceIdxToRegister];
  }
  let referenceIdsToUnregister = Object.keys(referenceObjToUnregister);
  let referencesToUnregister = Object.values(referenceObjToUnregister);
  let numReferencesToUnregister = referenceIdsToUnregister.length;
  for (let referenceIdxToUnegister = 0; referenceIdxToUnegister < numReferencesToUnregister; ++referenceIdxToUnegister) {
    let referenceIdToUnregister = referenceIdsToUnregister[referenceIdxToUnegister];
    if (referencePool.getReference(referenceIdToUnregister)) {
      referencePool.unregisterReferences(referenceIdToUnregister);
    } else {
      let referenceToUnregister = referencesToUnregister[referenceIdxToUnegister];
      let allReferences = referencePool.getAllReferences();
      let referenceIdToUnregisterNew = Object.keys(allReferences).find((referenceId) => {
        let reference = allReferences[referenceId];
        return (reference.referFrom.isSameAs(referenceToUnregister.referFrom) &&
          reference.referTo.isSameAs(referenceToUnregister.referTo));
      });
      referencePool.unregisterReferences(referenceIdToUnregisterNew);
      delete referenceObjToUnregister[referenceIdToUnregister];
      referenceObjToUnregister[referenceIdToUnregisterNew] = referenceToUnregister;
    }
  }
  ChangeHistory.instance.registerReferencePoolCallbacks();
}

function referencePoolOnRegisterCallback(registeredReferenceIds, registeredReferences) {
  let numRegisteredReferences = registeredReferenceIds.length;
  if (numRegisteredReferences === 0) return;
  let registeredReferenceObj = {};
  for (let registeredReferenceIdx = 0; registeredReferenceIdx < numRegisteredReferences; ++registeredReferenceIdx) {
    let registeredReferenceId = registeredReferenceIds[registeredReferenceIdx];
    let registeredReference = registeredReferences[registeredReferenceIdx];
    registeredReferenceObj[registeredReferenceId] = registeredReference;
  }
  let change = new Change('registering references', undefined, undefined, registeredReferenceObj, undefined, undefined, undefined);
  ChangeHistory.instance.register(change);
}

function referencePoolOnUnregisterCallback(unregisteredReferenceIds, unregisteredReferences) {
  let numRegisteredReferences = unregisteredReferenceIds.length;
  if (numRegisteredReferences === 0) return;
  let unregisteredReferenceObj = {};
  for (let unregisteredReferenceIdx = 0; unregisteredReferenceIdx < numRegisteredReferences; ++unregisteredReferenceIdx) {
    let unregisteredReferenceId = unregisteredReferenceIds[unregisteredReferenceIdx];
    let unregisteredReference = unregisteredReferences[unregisteredReferenceIdx];
    unregisteredReferenceObj[unregisteredReferenceId] = unregisteredReference;
  }
  let change = new Change('unregistering references', undefined, undefined, undefined, unregisteredReferenceObj, undefined, undefined);
  ChangeHistory.instance.register(change);
}

class AtomicChanges {
  static name = 'AtomicChanges';

  constructor(summary, changes, undoCallback, redoCallback) {
    this.summary = summary;
    this.changes = changes;
    this.undoCallback = undoCallback;
    this.redoCallback = redoCallback;
  }

  hasNoChange() {
    for (let change of this.changes) {
      if (!change.hasNoChange()) return false;
    }
    return true;
  }

  undo(scattData) {
    let numChanges = this.changes.length;
    for (let changeIdx = (numChanges - 1); changeIdx >= 0; --changeIdx) {
      let change = this.changes[changeIdx];
      change.undo(scattData);
    }
    if (this.undoCallback !== undefined) this.undoCallback();
  }

  redo(scattData) {
    for (let change of this.changes) {
      change.redo(scattData);
    }
    if (this.redoCallback !== undefined) this.redoCallback();
  }
}

class Change {
  static name = 'Change';

  constructor(summary, currentScattData, previousScattData, registeredReference, unregisteredReference, undoCallback, redoCallback) {
    let addedTimelineData = {};
    let deletedTimelineData = {};
    let updatedTimelineDataDifferenceBefore = {};
    let updatedTimelineDataDifferenceAfter = {};
    if (!((currentScattData === undefined) && (previousScattData === undefined))) {
      let currentTimelineDataSet = (currentScattData === undefined)? {} : currentScattData.timelineDataSet;
      let previousTimelineDataSet = (previousScattData === undefined)? {} : previousScattData.timelineDataSet;
      let keptTimelineDataIds = [];
      let currentTimelineDataIds = Object.keys(currentTimelineDataSet);
      let previousTimelineDataIds = Object.keys(previousTimelineDataSet);
      for (let currentTimelineDataId of currentTimelineDataIds) {
        if (!previousTimelineDataIds.includes(currentTimelineDataId)) {
          let currentTimelineData = currentTimelineDataSet[currentTimelineDataId];
          if (currentTimelineData.readonly) continue;
          addedTimelineData[currentTimelineDataId] = currentTimelineData.clone(true);
        } else {
          keptTimelineDataIds.push(currentTimelineDataId);
        }
      }
      for (let previousTimelineDataId of previousTimelineDataIds) {
        if (!currentTimelineDataIds.includes(previousTimelineDataId)) {
          let previousTimelineData = previousTimelineDataSet[previousTimelineDataId];
          if (previousTimelineData.readonly) continue;
          deletedTimelineData[previousTimelineDataId] = previousTimelineData.clone(true);
        }
      }
      for (let keptTimelineDataId of keptTimelineDataIds) {
        let currentTimelineData = currentTimelineDataSet[keptTimelineDataId];
        let previousTimelineData = previousTimelineDataSet[keptTimelineDataId];
        if (currentTimelineData.readonly || previousTimelineData.readonly) continue;
        let timelineDifferencePair = previousTimelineData.getDifferencePair(currentTimelineData);
        if (timelineDifferencePair !== undefined) {
          updatedTimelineDataDifferenceBefore[keptTimelineDataId] = timelineDifferencePair.before;
          updatedTimelineDataDifferenceAfter[keptTimelineDataId] = timelineDifferencePair.after;
        }
      }
    }
    this.summary = summary;
    this.addedTimelineData = addedTimelineData;
    this.deletedTimelineData = deletedTimelineData;
    this.updatedTimelineDataDifferenceBefore = updatedTimelineDataDifferenceBefore;
    this.updatedTimelineDataDifferenceAfter = updatedTimelineDataDifferenceAfter;
    if (registeredReference === undefined) registeredReference = {};
    if (unregisteredReference === undefined) unregisteredReference = {};
    this.registeredReference = registeredReference;
    this.unregisteredReference = unregisteredReference;
    this.undoCallback = undoCallback;
    this.redoCallback = redoCallback;
  }

  hasNoChange() {
    if (Object.keys(this.addedTimelineData).length > 0) return false;
    if (Object.keys(this.deletedTimelineData).length > 0) return false;
    if (Object.keys(this.updatedTimelineDataDifferenceBefore).length > 0) return false;
    if (Object.keys(this.updatedTimelineDataDifferenceAfter).length > 0) return false;
    if (Object.keys(this.registeredReference).length > 0) return false;
    if (Object.keys(this.unregisteredReference).length > 0) return false;
    return true;
  }

  undo(scattData) {
    applyChanges(
      scattData,
      this.addedTimelineData,
      this.deletedTimelineData,
      this.updatedTimelineDataDifferenceBefore,
      this.unregisteredReference,
      this.registeredReference,
    );
    if (this.undoCallback !== undefined) this.undoCallback();
  }

  redo(scattData) {
    applyChanges(
      scattData,
      this.deletedTimelineData,
      this.addedTimelineData,
      this.updatedTimelineDataDifferenceAfter,
      this.registeredReference,
      this.unregisteredReference,
    );
    if (this.redoCallback !== undefined) this.redoCallback();
  }
}

class ChangeHistory {
  static name = 'ChangeHistory';

  static instance = undefined;

  constructor(scattData, capacity = 1000) {
    this.capacity = capacity;
    this.writeIdx = 0;
    this.lastSavedIdx = undefined;
    this.data = [];
    this.tempData = [];
    this.currentScattData = scattData;
    this.onReferencePoolRegisterCallbackId = undefined;
    this.onReferencePoolUnregisterCallbackId = undefined;
  }

  get readIdx() {
    if (this.writeIdx === 0) return undefined;
    return this.writeIdx - 1;
  }

  get lastTempData() {
    if (!this.isTempDataSet) return undefined;
    return this.tempData[this.tempData.length - 1];
  }

  get isTempDataSet() {
    return this.tempData.length > 0;
  }

  initializeTempData() {
    this.tempData.push([]);
  }

  finalizeTempData(summary, undoCallback, redoCallback) {
    if (!this.isTempDataSet) return false;
    let lastTempData = this.tempData.pop();
    let atomicChanges = new AtomicChanges(summary, lastTempData, undoCallback, redoCallback);
    if (atomicChanges.hasNoChange()) return false;
    this.register(atomicChanges);
    return true;
  }

  register(change) {
    if (this.writeIdx === this.capacity) {
      this.data.splice(0, 1);
      if (this.writeIdx !== 0) --this.writeIdx;
    }
    let numData = this.data.length;
    if (this.writeIdx < numData) {
      let numDataToDelete = numData - this.writeIdx;
      this.data.splice(this.writeIdx, numDataToDelete);
    }
    if (this.isTempDataSet) {
      this.lastTempData.push(change);
    } else {
      this.data.push(change);
      ++this.writeIdx;
    }
  }

  clearRegistration() {
    let numRegistrations = this.data.length;
    this.data.splice(0, numRegistrations);
    this.writeIdx = 0;
  }

  get() {
    if (this.readIdx < 0) return undefined;
    if (this.readIdx >= this.data.length) return undefined;
    return this.data[this.readIdx];
  }

  forwardAndGet() {
    if (this.isPointingLastChange()) {
      return undefined;
    } else {
      ++this.writeIdx;
      let ret = this.get();
      return ret;
    }
  }

  getAndBackward() {
    if (this.isPointingFirstChange()) {
      return undefined;
    } else {
      let ret = this.get();
      --this.writeIdx;
      return ret;
    }
  }

  isPointingFirstChange() {
    return (this.writeIdx === 0);
  }

  isPointingLastChange() {
    return (this.writeIdx === this.data.length);
  }

  registerReferencePoolCallbacks() {
    this.onReferencePoolRegisterCallbackId = referencePool.registerOnRegisterCallback(referencePoolOnRegisterCallback);
    this.onReferencePoolUnregisterCallbackId = referencePool.registerOnUnregisterCallback(referencePoolOnUnregisterCallback);
  }

  unregisterReferencePoolCallbacks() {
    referencePool.unregisterOnRegisterCallback(this.onReferencePoolRegisterCallbackId);
    referencePool.unregisterOnUnregisterCallback(this.onReferencePoolUnregisterCallbackId);
    this.onReferencePoolRegisterCallbackId = undefined;
    this.onReferencePoolUnregisterCallbackId = undefined;
  }
}

export default {
  initialize(initialScattData) {
    ChangeHistory.instance = new ChangeHistory(initialScattData.clone(true));
    ChangeHistory.instance.registerReferencePoolCallbacks();
  },

  finalize() {
    ChangeHistory.instance.unregisterReferencePoolCallbacks();
    ChangeHistory.instance = undefined;
  },

  isFirstChange() {
    return ChangeHistory.instance.isPointingFirstChange();
  },

  isLastChange() {
    return ChangeHistory.instance.isPointingLastChange();
  },

  undo(scattData) {
    if (ChangeHistory.instance.isTempDataSet) return undefined;
    let change = ChangeHistory.instance.getAndBackward();
    if (!change) return undefined;
    ChangeHistory.instance.currentScattData = scattData.clone(true);
    change.undo(ChangeHistory.instance.currentScattData);
    return new ChangeHistoryResult(change.summary, ChangeHistory.instance.currentScattData.clone(true));
  },

  redo(scattData) {
    if (ChangeHistory.instance.isTempDataSet) return undefined;
    let change = ChangeHistory.instance.forwardAndGet();
    if (change === undefined) return undefined;
    ChangeHistory.instance.currentScattData = scattData.clone(true);
    change.redo(ChangeHistory.instance.currentScattData);
    return new ChangeHistoryResult(change.summary, ChangeHistory.instance.currentScattData.clone(true));
  },

  markAsSaved() {
    ChangeHistory.instance.lastSavedIdx = ChangeHistory.instance.readIdx;
  },

  isSameAsLastSaved() {
    return ChangeHistory.instance.lastSavedIdx === ChangeHistory.instance.readIdx;
  },

  beginAtomicChanges() {
    ChangeHistory.instance.initializeTempData();
  },

  endAtomicChanges(summary, undoCallback = undefined, redoCallback = undefined) {
    return ChangeHistory.instance.finalizeTempData(summary, undoCallback, redoCallback);
  },

  areAtomicChangesStarted() {
    return ChangeHistory.instance.isTempDataSet;
  },

  registerChange(scattData, summary, undoCallback = undefined, redoCallback = undefined) {
    let change = new Change(summary, scattData, ChangeHistory.instance.currentScattData, undefined, undefined, undoCallback, redoCallback);
    if (change.hasNoChange()) return false;
    ChangeHistory.instance.register(change);
    ChangeHistory.instance.currentScattData = scattData.clone(true);
    return true;
  },

  clearChangeRegistration() {
    if (ChangeHistory.instance.isTempDataSet) return;
    ChangeHistory.instance.clearRegistration();
  },

  debugGetData() {
    return ChangeHistory.instance.data;
  },
}